/**
 * FORGE-264 — Rich offer content parser.
 *
 * Pure function that takes the raw HTML coming from OrderPageItemChoice__c.Argument1__c
 * and returns an ordered array of renderable segments:
 *
 *   { kind: 'html', value: '<p>...</p>' }
 *   { kind: 'cta',  choiceId: 'a0Z...', buttonHtml: '<span ...>Label</span>' }
 *
 * Visually hidden CTA markers (display:none / visibility:hidden / off-screen
 * inline styles on the marker or its .op-cta-button) still remove the marker
 * from the HTML stream but do not emit a `cta` segment, so the shop does not
 * mount CheckoutButtonProcessOrder (no disclaimer / skip chrome).
 *
 * Exported as a standalone module so it can be unit-tested with Vitest
 * without mounting the Vue component.
 */

/**
 * Normalize an inline style attribute for substring checks.
 * @param {string|null} styleAttr
 * @returns {string}
 */
function normalizeInlineStyle(styleAttr) {
  return String(styleAttr || "")
    .toLowerCase()
    .replace(/\s+/g, "");
}

/**
 * True when an element's inline style is an intentional visual hide.
 * Only inline styles are considered (DOMParser has no computed styles).
 * @param {string|null} styleAttr
 * @returns {boolean}
 */
export function isVisuallyHiddenStyle(styleAttr) {
  const s = normalizeInlineStyle(styleAttr);
  if (!s) return false;
  if (s.includes("display:none")) return true;
  if (s.includes("visibility:hidden")) return true;

  // Require a style property boundary so margin-left / padding-left do not match.
  const offscreen = /(?:^|;)left:-?\d{4,}px/.test(s);
  if (!offscreen) return false;

  const collapsed =
    s.includes("overflow:hidden") ||
    /(?:^|;)(?:width|height):0(?:px|%)?(?:!important)?(?:;|$)/.test(s) ||
    /(?:^|;)(?:width|height):1px(?:!important)?(?:;|$)/.test(s);
  if (collapsed) return true;
  // Common ops pattern: position:absolute|fixed;left:-9999px (optionally with tiny box).
  return s.includes("position:absolute") || s.includes("position:fixed");
}

/**
 * True when a .op-cta marker (or its .op-cta-button child) is visually hidden.
 * @param {Element} marker
 * @returns {boolean}
 */
export function isVisuallyHiddenCta(marker) {
  if (!marker) return false;
  if (isVisuallyHiddenStyle(marker.getAttribute("style"))) return true;
  const span = marker.querySelector(".op-cta-button");
  return Boolean(span && isVisuallyHiddenStyle(span.getAttribute("style")));
}

/**
 * @param {string} html
 * @param {string} [fallbackChoiceId]
 * @param {Object} [options]
 * @param {string} [options.fallbackButtonLabel] - i18n label injected by the
 *   caller (e.g. `$t('labels.ctaDefault')`) and used when a CTA marker is
 *   present but the inner span is missing / empty. Defaults to an empty
 *   string so CheckoutButtonProcessOrder falls back to its own i18n label
 *   instead of leaking French into non-FR locales.
 * @returns {Array<{kind:'html'|'cta', value?:string, choiceId?:string, buttonHtml?:string}>}
 */
export function parseRichOfferContent(
  html,
  fallbackChoiceId = "",
  options = {},
) {
  const fallbackButtonLabel = options.fallbackButtonLabel || "";
  if (!html || typeof html !== "string") return [];

  if (
    typeof window === "undefined" ||
    typeof window.DOMParser === "undefined"
  ) {
    return [{ kind: "html", value: html }];
  }

  const doc = new window.DOMParser().parseFromString(
    `<div id="__rich-root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("__rich-root");
  if (!root) {
    return [{ kind: "html", value: html }];
  }

  const markers = Array.from(root.querySelectorAll(".op-cta"));
  if (markers.length === 0) {
    return [{ kind: "html", value: html }];
  }

  // Collect CTA data and record each marker's position using a sentinel node.
  // We walk the DOM tree directly (no innerHTML re-serialisation) to avoid
  // double-encoding of HTML entities (&amp; → &amp;amp; etc.).
  const ctaData = [];
  markers.forEach((marker, i) => {
    const choiceId =
      (marker.getAttribute("data-choice-id") || "").trim() ||
      fallbackChoiceId ||
      "";
    const span = marker.querySelector(".op-cta-button");
    // If the marker has no inner span AND no innerHTML, fall back to the
    // caller-provided (i18n) label rather than hardcoding French. Downstream,
    // CheckoutButtonProcessOrder also falls back to its own i18n when
    // `processordertext` is empty.
    const buttonHtml = span
      ? span.outerHTML
      : marker.innerHTML || fallbackButtonLabel;
    ctaData.push({
      choiceId,
      buttonHtml,
      hidden: isVisuallyHiddenCta(marker),
    });

    // Replace the marker with a sentinel comment so we can locate the split
    // point while walking the DOM without re-serialising.
    const sentinel = doc.createComment(`__OP_CTA_${i}__`);
    marker.parentNode.replaceChild(sentinel, marker);
  });

  // Walk all child nodes of root in document order, emitting segments.
  const segments = [];
  let htmlBuffer = "";

  const flushHtml = () => {
    if (htmlBuffer.length > 0) {
      segments.push({ kind: "html", value: htmlBuffer });
      htmlBuffer = "";
    }
  };

  const escapeText = (text) =>
    text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const VOID_TAGS = new Set([
    "area",
    "base",
    "br",
    "col",
    "embed",
    "hr",
    "img",
    "input",
    "link",
    "meta",
    "param",
    "source",
    "track",
    "wbr",
  ]);

  const SENTINEL_RE = /^__OP_CTA_\d+__$/;

  // Single-pass pre-computation of which ancestor elements contain a sentinel
  // (directly or transitively). We populate a Set of element refs so the
  // per-element check during `walkChildren()` is O(1) instead of re-walking
  // the full subtree for every element (which was O(n^2) on large rich HTML).
  //
  // We deliberately do NOT use a TreeWalker with SHOW_COMMENT: some runtimes
  // (notably happy-dom used in unit tests) implement SHOW_COMMENT incorrectly
  // and skip comment nodes silently. A plain recursive DFS visits each node
  // exactly once and works identically across real browsers, jsdom and happy-dom.
  const sentinelAncestors = new Set();
  const mark = (el) => {
    for (const n of el.childNodes) {
      if (n.nodeType === Node.COMMENT_NODE && SENTINEL_RE.test(n.nodeValue)) {
        let a = el;
        while (a && a !== root && !sentinelAncestors.has(a)) {
          sentinelAncestors.add(a);
          a = a.parentNode;
        }
      } else if (n.nodeType === Node.ELEMENT_NODE) {
        mark(n);
      }
    }
  };
  mark(root);
  const containsSentinel = (el) => sentinelAncestors.has(el);

  // Serialize a non-sentinel subtree back to HTML without going through
  // root.innerHTML (which would re-encode the whole document). Text nodes
  // MUST be HTML-escaped: DOMParser decodes entities on ingestion (&lt;→<),
  // so re-emitting textContent raw would turn user-typed `&lt;script&gt;`
  // into an executable tag in the downstream v-html render.
  const serializeNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return escapeText(node.textContent);
    if (node.nodeType === Node.COMMENT_NODE) return "";
    const el = /** @type {Element} */ (node);
    const attrs = Array.from(el.attributes)
      .map((a) => ` ${a.name}="${a.value.replace(/"/g, "&quot;")}"`)
      .join("");
    const tag = el.tagName.toLowerCase();
    if (VOID_TAGS.has(tag)) return `<${tag}${attrs}>`;
    const inner = Array.from(el.childNodes).map(serializeNode).join("");
    return `<${tag}${attrs}>${inner}</${tag}>`;
  };

  const walkChildren = (parent) => {
    for (const child of Array.from(parent.childNodes)) {
      if (child.nodeType === Node.COMMENT_NODE) {
        const m = child.nodeValue.match(SENTINEL_RE);
        if (m) {
          const idx = parseInt(child.nodeValue.match(/\d+/)[0], 10);
          const cta = ctaData[idx];
          // Hidden markers stay out of the render stream so RichOfferContent
          // never mounts CheckoutButtonProcessOrder (disclaimer / skip).
          // Skip flushHtml when hidden so surrounding HTML stays one segment.
          if (cta && cta.choiceId && !cta.hidden) {
            flushHtml();
            segments.push({
              kind: "cta",
              choiceId: cta.choiceId,
              buttonHtml: cta.buttonHtml,
            });
          }
          continue;
        }
      }
      if (child.nodeType === Node.ELEMENT_NODE) {
        // If this element (or a descendant) holds a sentinel, we MUST recurse
        // to emit the CTA segment at the right position. Otherwise we can
        // serialize the whole subtree as-is and keep buffering. Note: we walk
        // childNodes (not querySelector) because sentinels are comment nodes.
        if (containsSentinel(child)) {
          const el = /** @type {Element} */ (child);
          const attrs = Array.from(el.attributes)
            .map((a) => ` ${a.name}="${a.value.replace(/"/g, "&quot;")}"`)
            .join("");
          const tag = el.tagName.toLowerCase();
          htmlBuffer += `<${tag}${attrs}>`;
          walkChildren(child);
          htmlBuffer += `</${tag}>`;
        } else {
          htmlBuffer += serializeNode(child);
        }
      } else if (child.nodeType === Node.TEXT_NODE) {
        htmlBuffer += escapeText(child.textContent);
      }
    }
  };

  walkChildren(root);
  flushHtml();

  return segments;
}

/**
 * Quick predicate: does the HTML contain at least one op-cta marker?
 * Used by Product.vue to switch between legacy 3-field rendering and
 * the new rich-content path.
 *
 * We explicitly avoid RegExp lookbehind `(?<!...)` here because it is
 * a hard SyntaxError at bundle parse time on Safari ≤16.3 (iOS users
 * on older phones), which would brick the whole shop page, not just
 * rich-content rendering. Instead we:
 *   1. Extract every `class="…"` / `class='…'` attribute value with a
 *      lookbehind-free regex.
 *   2. Split the value on whitespace and test for exact token `op-cta`.
 * This is stricter than the server-side Apex CTA_MARKER_REGEX (which
 * runs on JVM and can afford lookbehinds) — both reject `op-cta-button`,
 * `op-ctaholder`, `my-op-cta`, etc. and accept `op-cta` as a standalone
 * class token. Must stay aligned with the parser's `querySelectorAll(".op-cta")`
 * (which is already tag-agnostic and token-exact).
 *
 * @param {string} html
 * @returns {boolean}
 */
const CLASS_ATTR_RE = /<[a-z][^>]*\sclass\s*=\s*["']([^"']+)["']/gi;
export function hasRichOfferMarker(html) {
  if (!html || typeof html !== "string") return false;
  // Reset lastIndex because CLASS_ATTR_RE is a module-level /g regex.
  CLASS_ATTR_RE.lastIndex = 0;
  let m;
  while ((m = CLASS_ATTR_RE.exec(html)) !== null) {
    // Exact-token check — avoids the `op-cta-button` / `op-ctaholder`
    // false positives that `\bop-cta\b` produced.
    if (m[1].split(/\s+/).indexOf("op-cta") !== -1) return true;
  }
  return false;
}
